import { Button, Callout, Card, IconButton, Input, Select } from '@apovisio/ui';
import { displayNameSchema } from '@apovisio/contracts';
import type { GuestState } from '@apovisio/contracts';
import { useMediaDevices, usePreviewTracks } from '@livekit/components-react';
import { LocalAudioTrack, LocalVideoTrack, isBrowserSupported } from 'livekit-client';
import type { LocalTrack } from 'livekit-client';
import { Mic, MicOff, RefreshCw, Video, VideoOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { BrandMark } from '../../app/BrandMark';
import { PageHeading } from '../../components/PageHeading';
import { formatSlot } from '../../lib/format';
import { reportClientError } from '../../lib/telemetry';
import { loadDevicePreferences, saveDevicePreferences } from './device-preferences';
import type { DevicePreferences } from './device-preferences';
import { MEDIA_PROBLEM_MESSAGES, MEDIA_PROBLEM_REASONS, classifyMediaError } from './media-errors';
import type { MediaProblem } from './media-errors';

/** Suggestion de nom : partie du libellé avant « — » (« Cécile — ABG » → « Cécile »). */
function suggestedName(state: GuestState): string {
  if (state.displayName) return state.displayName;
  const [first] = state.invitation.label.split(/\s+[—–-]\s+/);
  return (first ?? '').trim().slice(0, 60);
}

function PermissionHelp() {
  return (
    <details>
      <summary>Comment autoriser la caméra et le micro ?</summary>
      <ul className="av-text-sm app-help-list">
        <li>Chrome, Edge : icône à gauche de l’adresse → Caméra et Micro → Autoriser.</li>
        <li>Firefox : icône de caméra barrée dans la barre d’adresse → Autoriser.</li>
        <li>Safari (Mac) : Safari → Réglages pour ce site web → Caméra et Micro → Autoriser.</li>
        <li>Safari (iPhone, iPad) : « aA » dans la barre d’adresse → Réglages du site web.</li>
      </ul>
      <p className="av-text-sm">Rechargez ensuite la page ou cliquez sur « Réessayer ».</p>
    </details>
  );
}

/** Niveau du micro (0–100) mesuré localement, sans rien transmettre. */
function useMicLevel(track: LocalAudioTrack | undefined): number {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!track) return;
    const AudioContextClass = window.AudioContext as typeof AudioContext | undefined;
    if (!AudioContextClass) return;
    const context = new AudioContextClass();
    const source = context.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    let frame = 0;
    const tick = () => {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += ((sample - 128) / 128) ** 2;
      setLevel(Math.min(100, Math.round(Math.sqrt(sum / samples.length) * 400)));
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      void context.close();
    };
  }, [track]);
  return track ? level : 0;
}

export interface PrejoinViewProps {
  state: GuestState;
  pending: boolean;
  error: string | null;
  onRequest: (displayName: string, devices: DevicePreferences) => void;
}

/**
 * Écran 6 — Pré-join : nom, aperçu caméra, choix des périphériques, test micro. Aucune
 * connexion à la salle média : seuls les périphériques locaux sont ouverts.
 */
export function PrejoinView({ state, pending, error, onRequest }: PrejoinViewProps) {
  const supported = useMemo(() => isBrowserSupported(), []);
  const [name, setName] = useState(() => suggestedName(state));
  const [nameError, setNameError] = useState<string | undefined>();
  const [devices, setDevices] = useState<DevicePreferences>(loadDevicePreferences);
  const [problem, setProblem] = useState<MediaProblem | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack | undefined>();
  const micLevel = useMicLevel(audioTrack);
  const cameras = useMediaDevices({ kind: 'videoinput' });
  const microphones = useMediaDevices({ kind: 'audioinput' });
  const onProblem = useCallback((value: MediaProblem) => {
    setProblem(value);
    if (value !== 'other') reportClientError(MEDIA_PROBLEM_REASONS[value]);
  }, []);

  useEffect(() => {
    if (!supported) reportClientError('browser_unsupported');
  }, [supported]);

  useEffect(() => {
    saveDevicePreferences(devices);
  }, [devices]);

  const update = (patch: Partial<DevicePreferences>) => {
    setProblem(null);
    setDevices((current) => ({ ...current, ...patch }));
  };

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = displayNameSchema.safeParse(name);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message);
      return;
    }
    setNameError(undefined);
    onRequest(parsed.data, devices);
  }

  const problemMessage = problem ? MEDIA_PROBLEM_MESSAGES[problem] : null;

  return (
    <main className="app-centered">
      <div className="app-prejoin">
        <div className="av-stack">
          <BrandMark subtitle="Apogée Consult vous invite" />
          <MediaPreview
            key={attempt}
            devices={devices}
            onToggle={update}
            onProblem={onProblem}
            onAudioTrack={setAudioTrack}
          />
          <p className="av-text-secondary av-text-sm">
            L’aperçu reste sur votre appareil : rien n’est transmis avant votre admission.
          </p>
        </div>

        <Card padding="lg" as="div">
          <div className="av-stack av-stack--sm">
            <PageHeading documentTitle={state.meeting.title}>{state.meeting.title}</PageHeading>
            <p className="av-text-secondary">
              {formatSlot(state.meeting.scheduledStart, state.meeting.scheduledEnd)}
            </p>
          </div>

          {!supported ? (
            <Callout tone="danger" title="Votre navigateur ne permet pas la visio." live="alert">
              Utilisez une version récente de Chrome, Edge, Firefox ou Safari.
            </Callout>
          ) : null}
          {problemMessage ? (
            <Callout
              tone={problem === 'missing' ? 'info' : 'warning'}
              title={problemMessage.title}
              live="alert"
            >
              {problem === 'blocked' ? <PermissionHelp /> : problemMessage.body}
            </Callout>
          ) : null}
          {error ? <Callout tone="danger" title={error} live="alert" /> : null}

          <form className="av-stack" onSubmit={submit} noValidate>
            <Input
              label="Votre nom"
              value={name}
              maxLength={60}
              autoComplete="name"
              hint="Visible par les participants."
              onChange={(event) => {
                setName(event.target.value);
              }}
              error={nameError}
            />
            <Select
              label="Caméra"
              value={devices.videoDeviceId ?? ''}
              disabled={cameras.length === 0}
              onChange={(event) => {
                update({ videoDeviceId: event.target.value || undefined });
              }}
            >
              {cameras.length === 0 ? <option value="">Aucune caméra disponible</option> : null}
              {cameras.map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Caméra ${String(index + 1)}`}
                </option>
              ))}
            </Select>
            <Select
              label="Micro"
              value={devices.audioDeviceId ?? ''}
              disabled={microphones.length === 0}
              onChange={(event) => {
                update({ audioDeviceId: event.target.value || undefined });
              }}
            >
              {microphones.length === 0 ? <option value="">Aucun micro disponible</option> : null}
              {microphones.map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Micro ${String(index + 1)}`}
                </option>
              ))}
            </Select>
            <div className="av-stack av-stack--sm">
              <span className="av-field__label" id="mic-level-label">
                Niveau du micro
              </span>
              <div
                className="app-meter"
                role="meter"
                aria-labelledby="mic-level-label"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={micLevel}
              >
                <div className="app-meter__level" style={{ width: `${String(micLevel)}%` }} />
              </div>
              <span className="av-text-secondary av-text-sm">
                {audioTrack ? 'Parlez : la barre doit bouger.' : 'Micro coupé ou indisponible.'}
              </span>
            </div>
            {problem === 'blocked' || problem === 'busy' || problem === 'other' ? (
              <Button
                variant="secondary"
                icon={<RefreshCw />}
                onClick={() => {
                  setProblem(null);
                  setAttempt((value) => value + 1);
                }}
              >
                Réessayer
              </Button>
            ) : null}
            <Button type="submit" size="lg" fullWidth loading={pending} disabled={!supported}>
              Demander à rejoindre
            </Button>
            <p className="av-text-secondary av-text-sm">L’organisateur validera votre entrée.</p>
          </form>
        </Card>
      </div>
    </main>
  );
}

/**
 * Aperçu local (clé = tentative : « Réessayer » recrée les pistes). Les pistes sont arrêtées
 * au démontage par usePreviewTracks.
 */
function MediaPreview({
  devices,
  onToggle,
  onProblem,
  onAudioTrack,
}: {
  devices: DevicePreferences;
  onToggle: (patch: Partial<DevicePreferences>) => void;
  onProblem: (problem: MediaProblem) => void;
  onAudioTrack: (track: LocalAudioTrack | undefined) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const options = useMemo(
    () => ({
      video: devices.videoEnabled ? { deviceId: devices.videoDeviceId } : false,
      audio: devices.audioEnabled ? { deviceId: devices.audioDeviceId } : false,
    }),
    [devices.videoEnabled, devices.videoDeviceId, devices.audioEnabled, devices.audioDeviceId],
  );
  const onError = useCallback(
    (failure: Error) => {
      onProblem(classifyMediaError(failure));
    },
    [onProblem],
  );
  const tracks: LocalTrack[] | undefined = usePreviewTracks(options, onError);
  const videoTrack = tracks?.find(
    (track): track is LocalVideoTrack => track instanceof LocalVideoTrack,
  );
  const audioTrack = tracks?.find(
    (track): track is LocalAudioTrack => track instanceof LocalAudioTrack,
  );

  useEffect(() => {
    onAudioTrack(audioTrack);
  }, [audioTrack, onAudioTrack]);

  useEffect(() => {
    const element = videoRef.current;
    if (!videoTrack || !element) return;
    videoTrack.attach(element);
    return () => {
      videoTrack.detach(element);
    };
  }, [videoTrack]);

  return (
    <div className="app-preview av-theme-room">
      {videoTrack ? (
        <video
          ref={videoRef}
          className="app-preview__video"
          muted
          playsInline
          aria-label="Aperçu de votre caméra"
        />
      ) : (
        <span className="app-preview__placeholder">
          {devices.videoEnabled ? 'Caméra indisponible' : 'Caméra coupée'}
        </span>
      )}
      <div className="app-preview__controls">
        <IconButton
          label={devices.audioEnabled ? 'Couper le micro' : 'Activer le micro'}
          pressed={devices.audioEnabled}
          variant="filled"
          onClick={() => {
            onToggle({ audioEnabled: !devices.audioEnabled });
          }}
        >
          {devices.audioEnabled ? <Mic /> : <MicOff />}
        </IconButton>
        <IconButton
          label={devices.videoEnabled ? 'Couper la caméra' : 'Activer la caméra'}
          pressed={devices.videoEnabled}
          variant="filled"
          onClick={() => {
            onToggle({ videoEnabled: !devices.videoEnabled });
          }}
        >
          {devices.videoEnabled ? <Video /> : <VideoOff />}
        </IconButton>
      </div>
    </div>
  );
}
